<template>
  <div class="card ml-2">
    <div
      class="header border-bottom accordian-toggle"
      v-b-toggle.verifyAndUpgrade
    >
      <h5 class="text-primary font-weight-normal mb-0">
        Verify and Upgrade to Silver
      </h5>
    </div>
    <b-collapse id="verifyAndUpgrade">
      <div class="body">
        <p class="text-muted small mb-3">
          <i class="fa fa-info-circle mr-1"></i>
          Enter the IDScan journey reference for this customer. The document is
          checked against their profile before the upgrade is applied.
        </p>

        <div class="form-group">
          <span class="float-label">
            <label for="journeyId">Enter Journey Id</label>
            <input
              id="journeyId"
              v-model="journeyId"
              type="text"
              class="form-control"
              autocomplete="off"
              :disabled="submitting"
              @keyup.enter="handleSubmit"
            />
          </span>
        </div>

        <b-button
          variant="primary"
          size="sm"
          :disabled="!canSubmit"
          @click="handleSubmit"
        >
          <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
          <i v-else class="fa fa-check mr-1"></i>
          Verify and Upgrade
        </b-button>
      </div>
    </b-collapse>

    <!--
      ENG-2416: the document was not accepted automatically, so ops decide. The reason field
      only appears once they have confirmed, so the decision and the justification are two
      deliberate steps rather than one form to fill in absently.
    -->
    <b-modal
      id="idScanReview"
      size="lg"
      no-stacking
      no-close-on-backdrop
      hide-footer
    >
      <template #modal-title>
        <h5 class="text-primary font-weight-normal mb-0">
          Review before upgrading to Silver
        </h5>
      </template>

      <div class="modal-area px-3 py-2" v-if="review">
        <div class="alert alert-warning">
          <p v-if="review.initiatedBy" class="mb-1">
            The system detected no customer number in the IDScan response. This
            scan was not initiated by the customer, but by
            <strong>{{ review.initiatedBy }}</strong>.
          </p>
          <span
            v-if="
              review.outcome === 'NOT_PASSED' ||
              review.outcome === 'PLATFORM_INITIATED'
            "
          >
            IDScan returned
            <strong>{{ review.highLevelResult || "no result" }}</strong>
            for this journey.
          </span>
          <span v-else>
            IDScan passed this document, but it does not match the customer's
            record.
          </span>
        </div>

        <!--
          ENG-2512: nothing ties a staff-initiated journey to this customer, so ops compare the
          person on the document with the person on our record before deciding.
        -->
        <div v-if="review.initiatedBy" class="mb-3">
          <h6 class="mb-2">Document compared with our record</h6>
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th class="small text-muted font-weight-normal"></th>
                <th class="small">IDScan document</th>
                <th class="small">Our record</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in comparisonRows" :key="row.label">
                <td class="small text-muted">{{ row.label }}</td>
                <td class="small" style="word-break: break-word">{{ row.document || "--" }}</td>
                <td class="small" style="word-break: break-word">{{ row.record || "--" }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="documentRows.length" class="mb-3">
          <h6 class="mb-2">Document read by IDScan</h6>
          <div
            v-for="row in documentRows"
            :key="row.label"
            class="d-flex justify-content-between flex-wrap"
          >
            <span class="small text-muted pr-2">{{ row.label }}</span>
            <span class="small" style="word-break: break-word">{{ row.value }}</span>
          </div>
        </div>

        <div v-if="providerChecks.length" class="mb-3">
          <h6 class="mb-2">Checks IDScan failed</h6>
          <ul class="mb-0 pl-3">
            <li
              v-for="check in providerChecks"
              :key="check.name"
              class="small"
              style="word-break: break-word"
            >
              {{ check.name }}
              <span class="text-muted" v-if="check.detail">— {{ check.detail }}</span>
            </li>
          </ul>
        </div>

        <div v-if="failedChecks.length" class="mb-3">
          <h6 class="mb-2">What did not match our record</h6>
          <ul class="mb-0 pl-3">
            <li
              v-for="check in failedChecks"
              :key="check.name"
              class="small"
              style="word-break: break-word"
            >
              {{ check.detail || check.name }}
            </li>
          </ul>
        </div>

        <p class="mb-3">
          Are you sure you want to upgrade this customer to Silver?
        </p>

        <div v-if="confirmed" class="form-group">
          <label for="approvalReason">Reason for upgrading</label>
          <textarea
            id="approvalReason"
            v-model="reason"
            rows="3"
            maxlength="1000"
            class="form-control"
            :disabled="submitting"
            placeholder="Explain why this document is acceptable"
          ></textarea>
        </div>

        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-secondary btn-mw mr-2"
            :disabled="submitting"
            @click="closeReview"
          >
            Cancel
          </b-button>
          <b-button
            v-if="!confirmed"
            variant="primary btn-mw"
            @click="confirmed = true"
          >
            Yes, continue
          </b-button>
          <b-button
            v-else
            variant="primary btn-mw"
            :disabled="!canApprove"
            @click="handleApprove"
          >
            <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
            Upgrade to Silver
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  VERIFY_IDSCAN_JOURNEY,
  APPROVE_IDSCAN_JOURNEY,
} from "@/store/actions.type.js";
import { myFunction } from "@/common/common-helper.js";

export default {
  name: "VerifyAndUpgrade",
  props: {
    customerId: {
      type: String,
      required: true,
    },
    customerDetails: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      journeyId: "",
      submitting: false,
      review: null,
      confirmed: false,
      reason: "",
    };
  },
  computed: {
    canSubmit() {
      return !this.submitting && this.journeyId.trim().length > 0;
    },
    canApprove() {
      return !this.submitting && this.reason.trim().length > 0;
    },
    failedChecks() {
      return (this.review?.checks || []).filter((check) => !check.passed);
    },
    /**
     * IDScan's own failed validations, which are not the same thing as `checks`: these are the
     * provider objecting to the document, `checks` is the document disagreeing with our record.
     * Ops need to see which of the two they are approving past.
     */
    providerChecks() {
      return this.review?.providerChecks || [];
    },
    /**
     * Only the document fields we actually received. A Refer journey usually carries a full
     * document, because the referral came from a separate check; a Notsupported one carries
     * nothing readable, and rows of "N/A" would tell ops less than showing no panel at all.
     */
    documentRows() {
      const document = this.review?.document || {};
      // The name and date of birth are already in the comparison table when it is shown.
      const compared = Boolean(this.review?.initiatedBy);
      return [
        { label: "Document", value: document.documentType },
        { label: "Number", value: document.idNumber },
        { label: "Name", value: compared ? null : [document.firstName, document.lastName].filter(Boolean).join(" ") },
        { label: "Date of birth", value: compared ? null : document.birthDate },
        { label: "Expires", value: document.expiryDate },
        { label: "Issued by", value: document.countryCode },
      ].filter((row) => row.value);
    },
    comparisonRows() {
      const document = this.review?.document || {};
      const customer = this.customerDetails || {};
      return [
        { label: "First name", document: document.firstName, record: customer.fname },
        { label: "Last name", document: document.lastName, record: customer.lname },
        { label: "Date of birth", document: document.birthDate, record: this.isoDate(customer.dob) },
      ];
    },
  },
  methods: {
    async handleSubmit() {
      if (!this.canSubmit) return;

      this.submitting = true;
      try {
        const response = await this.$store.dispatch(VERIFY_IDSCAN_JOURNEY, {
          customerId: this.customerId,
          journeyId: this.journeyId.trim(),
        });

        if (response?.status === 200) {
          this.handleOutcome(response.data);
        } else {
          myFunction.showInfo(this.failureMessage(response), "error");
        }
      } catch {
        myFunction.showInfo(
          "The verification could not be completed. Please try again.",
          "error",
        );
      } finally {
        this.submitting = false;
      }
    },

    /**
     * A 200 no longer means the customer was upgraded. Anything short of a clean pass comes
     * back as an outcome for ops to decide on, so this branches on `approvable` rather than
     * assuming success.
     */
    handleOutcome(details) {
      if (details?.approvable) {
        this.review = details;
        this.confirmed = false;
        this.reason = "";
        this.$bvModal.show("idScanReview");
        return;
      }

      myFunction.showInfo(this.outcomeMessage(details), "success");
      this.journeyId = "";
      this.$emit("upgraded");
    },

    outcomeMessage(details) {
      switch (details?.outcome) {
        case "NOT_REQUIRED":
          return "This customer already holds a valid verified ID. No upgrade was needed.";
        case "ALREADY_VERIFIED":
          return "This journey has already been verified for this customer. Nothing was changed.";
        case "GROUP_CORRECTED":
          return "This journey was already verified, but the customer was not on Silver. Their group has been corrected.";
        default:
          return "Customer verified and upgraded to Silver.";
      }
    },

    async handleApprove() {
      if (!this.canApprove) return;

      this.submitting = true;
      try {
        const response = await this.$store.dispatch(APPROVE_IDSCAN_JOURNEY, {
          customerId: this.customerId,
          journeyId: this.journeyId.trim(),
          reason: this.reason.trim(),
        });

        if (response?.status === 200) {
          myFunction.showInfo("Customer upgraded to Silver.", "success");
          this.closeReview();
          this.journeyId = "";
          this.$emit("upgraded");
        } else {
          myFunction.showInfo(this.failureMessage(response), "error");
        }
      } catch {
        myFunction.showInfo(
          "The upgrade could not be completed. Please try again.",
          "error",
        );
      } finally {
        this.submitting = false;
      }
    },

    /** Our record's date of birth in the same YYYY-MM-DD form IDScan reports the document's. */
    isoDate(value) {
      return value ? String(value).slice(0, 10) : null;
    },

    closeReview() {
      this.$bvModal.hide("idScanReview");
      this.review = null;
      this.confirmed = false;
      this.reason = "";
    },

    /**
     * The backend messages are already written for a reader, so they are shown
     * as they arrive. Only a response carrying no message at all is replaced.
     */
    failureMessage(response) {
      return (
        response?.data?.message ||
        "The verification could not be completed. Please try again."
      );
    },
  },
};
</script>
